import { cloneDeep } from 'lodash-es';
import type { PackageItemDisplayModel } from '@shared/models/packages/packageItems';
import type {
  IPackageItemImageNodeProperties,
  PackageItemImageNodeDisplayModel,
  PackageItemTextNodeDisplayModel,
} from '@shared/models/packages/packageNodes';
import type { CartPackageImageNodeDisplayModel } from '../models/carts/cartPackageImageNodes';
import type { PackageDisplayModel } from '../models/packages/packages';

/**
 * Only Autofill individual image nodes when they are Image1 or Image2
 * All other image nodes should prompt the user to select an image
 */
export const isAutoFilledIndividualImageNode = (imageNode: IPackageItemImageNodeProperties) => {
  const name = imageNode.name.toLowerCase();
  return name === 'image1' || name === 'image2';
};

export const isIndividualImageNode = (imageNode: IPackageItemImageNodeProperties) => {
  return imageNode.name.toLowerCase().startsWith('image') && !imageNode.static;
};
export const isGroupImageNode = (imageNode: IPackageItemImageNodeProperties) => {
  return imageNode.name.toLowerCase().startsWith('group') && !imageNode.static;
};
export const isCroppableNode = (imageNode: PackageItemImageNodeDisplayModel) => !!imageNode.positionCoords;

/**
 * Verify the imagenode is customizable by the consumer
 *
 * ***Rules:***
 * - Image name must be in the range of Image1 - Image98
 * - Image name must be in the range of Group1 - Group98
 */

export const isValidImageNode = (imageNode: CartPackageImageNodeDisplayModel | PackageItemImageNodeDisplayModel) => {
  // Name must contain Group or Image
  if (!imageNode.name.toLowerCase().includes('image') && !imageNode.name.toLowerCase().includes('group')) return false;

  // Test if the last two digits is a number and if it's lower than 99
  const doubleDigits = imageNode.name.slice(-2);
  if (Number.isInteger(parseInt(doubleDigits, 10)) && parseInt(doubleDigits, 10) < 99) return true;

  // Test if the last digit is a number
  const singleDigit = imageNode.name.slice(-1);
  if (Number.isInteger(parseInt(singleDigit, 10))) return true;

  return false;
};

export const getTextNodesForItem = (item: PackageItemDisplayModel): PackageItemTextNodeDisplayModel[] =>
  item.textNodes.filter((n) => !n.static);

export const getImageNodesForItem = (item: PackageItemDisplayModel): PackageItemImageNodeDisplayModel[] =>
  item.imageNodes.filter((n) => !n.static);

export const mergePackageItemTextNodes = (
  item: PackageItemDisplayModel,
  textNodeUpdates: PackageItemTextNodeDisplayModel[],
) => {
  const value = cloneDeep(item);
  value.textNodes = value.textNodes.map((tn) => ({
    ...tn,
    ...(textNodeUpdates.find((u) => u.id === tn.id) || {}),
  }));
  return value;
};

export const getAutoFillImageNodes = (pkg: PackageDisplayModel): PackageItemImageNodeDisplayModel[] => {
  return pkg.items
    .map((item) => getImageNodesForItem(item))
    .flat(2)
    .filter((n) => isAutoFilledIndividualImageNode(n) && isCroppableNode(n));
};
